import SubTitleText from "components/typography/subTitleText";
import { set } from "lodash";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Spinner } from "reactstrap";
import { VerifyTwoFactor, ResendTwoFactor, GetTwoFactorData } from "services/api/module/TwoFactorAuth";

const TwoFactorAuth = () => {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [uuid, setUuid] = useState("");
  // const [username, setUsername] = useState("");
  const [refCode, setRefCode] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [color, setColor] = useState("info");

  const [ isInvalid2FA, setIsInvalid2FA ] = useState(false);

  const [ loading, setLoading ] = useState(false);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds

  // Cooldown timer effect (always runs, even if tab is hidden)
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    const authParam = searchParams.get("auth") || "";
    setUuid(authParam);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.trim() === "") {
      setMessage("Please enter the code");
      return;
    }

    setMessage("Verifying code...");
    setLoading(true);
    try {
      // Call the API to verify the two-factor code
      const payload = { uuid, code };
      await API.fetchVerifyTwoFactor(payload);

      // setMessage("Code verified successfully!");
      // Redirect to the dashboard after success
      // window.location.href = "/dashboard";
    } catch (error) {
      console.error("Verification failed", error);
      setMessage("Verification failed. Please try again.");
    }

    setLoading(false);
  };

  const resendCode = async () => {
    setMessage("Resending verification code...");
    setResendLoading(true);
    try {
      // Call the API to resend the verification code
      const payload = { uuid };
      await API.fetchResendTwoFactor(payload);
      setColor("success");
      setMessage("Verification code has been resent to your email.");
      setResendCooldown(300); // 5 minutes cooldown
      setCode("")
    } catch (error) {
      console.error("Failed to resend code", error);
      setColor("danger");
      setMessage("Failed to resend verification code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const API = {
    fetchVerifyTwoFactor: async (payload) => {
      try {
        const response = await VerifyTwoFactor({
          data: {
            uuid: payload.uuid,
            code: payload.code,
          },
        });
        const resData = response.data;
        if (resData.status === 20000) {
          setColor("success");
          setMessage("Code verified successfully!");
          window.location.href = "/dashboard";
        }
        else {
          setColor("danger");
          setMessage("Verification failed. Please try again.");
        }
      } catch (err) {
        console.error("Verification error: ", err);
        throw err;
      }
    },
    fetchResendTwoFactor: async (payload) => {
      try {
        const response = await ResendTwoFactor({
          data: {
            uuid: payload.uuid,
          },
        });
        const resData = response.data;
        if (resData.status === 20000) {
          setSearchParams({ auth: resData.auth})

          setMessage("Verification code resent successfully.");
        }
      } catch (err) {
        console.error("Resend error: ", err);
        throw err;
      }
    },
    fetch2FAData: async (payload) => {
      try {
        const response = await GetTwoFactorData({
          params: {
            uuid: payload.uuid,
          },
        });
        const resData = response.data;
        if (resData.status === 20000) {
          setRefCode(resData.data.two_fa_refcode);

          return resData.data;
        }
        else {
          setIsInvalid2FA(true);
        }
      } catch (err) {
        console.error("2FA Data error: ", err);
        throw err;
      }
    },
  };

  useEffect(() => {
    if (uuid) {
      const payload = {
        uuid: uuid
      }
      
      API.fetch2FAData(payload)
    }
  }, [uuid])

  useEffect(() => {
    if (isInvalid2FA) {
      alert("Invalid two-factor authentication session. Please log in again.");
      window.location.href = "/login";
    }
  }, [isInvalid2FA]);

  return (
    <div className="account-pages my-5 pt-sm-5">
      <Container>
        <Row className="justify-content-center">
          <Col md={6}>
            <h2 className="text-center mt-4">Two-Factor Authentication</h2>
            <p className="text-center">
              Your account is protected with two-factor authentication.<br/>We’ve sent a verification code to your email.
            </p>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="twoFactorCode">Two-Factor Code <SubTitleText>REF: {refCode}</SubTitleText></Label>
                <Input
                  type="text"
                  id="twoFactorCode"
                  placeholder="Enter code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </FormGroup>
              <Button color="primary" type="submit" className="w-100">
                Continue <Spinner
                  color="light"
                  size="sm"
                  className={loading ? "d-inline-block" : "d-none"}
                />
              </Button>
            </Form>
            <Button
              color="link"
              className="mt-3 w-100"
              onClick={resendCode}
              disabled={resendCooldown > 0 || resendLoading}
            >
              {resendCooldown > 0
                ? `Resend available in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}`
                : 'Resend code via e-mail'}
              {resendLoading && (
                <Spinner color="primary" size="sm" />
              )}
            </Button>
            {message && <p className={`mt-3 text-center text-${color}`}>{message}</p>}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TwoFactorAuth;
